import { Badge, DataTable, type DataTableColumn } from "@design";
import { useQuery } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { type AdminTokenAuthorizationResponse, listAllAuthorizationsOptions } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { Text } from "@/components/Text";
import { isAuthorizationPurpose, needsReauth } from "@/features/account/authorizations";
import { formatDate, formatNumber } from "@/i18n/format";
import { AdminPage } from "../AdminPage";

type Authorization = AdminTokenAuthorizationResponse;

function UserCell({ row }: Readonly<{ row: Authorization }>) {
  const { t } = useTranslation("adminPlatform");
  return (
    <div className="flex min-w-0 flex-col text-left">
      <Text as="span" variant="body-sm" className="truncate">
        {row.userName || row.userEmail || t("authorizations.unknownUser", { id: row.userId })}
      </Text>
      {row.userEmail && (
        <Text as="span" variant="caption" tone="tertiary" className="truncate">
          {row.userEmail}
        </Text>
      )}
    </div>
  );
}

function useColumns(): DataTableColumn<Authorization>[] {
  const { t } = useTranslation("adminPlatform");
  return [
    {
      key: "user",
      header: t("authorizations.columns.user"),
      mobile: "primary",
      render: (row) => <UserCell row={row} />,
    },
    {
      key: "purpose",
      header: t("authorizations.columns.purpose"),
      render: (row) => (
        <Text as="span" variant="body-sm" tone="secondary">
          {isAuthorizationPurpose(row.purpose) ? t(`authorizations.purposes.${row.purpose}`) : row.purpose}
        </Text>
      ),
    },
    {
      key: "sources",
      header: t("authorizations.columns.sources"),
      render: (row) => (
        <Text as="span" variant="body-sm" tone="secondary">
          {formatNumber((row.syncTargets ?? []).length)}
        </Text>
      ),
    },
    {
      key: "lastUsed",
      header: t("authorizations.columns.lastUsed"),
      render: (row) => (
        <Text as="span" variant="body-sm" tone="tertiary">
          {row.lastUsedAt ? formatDate(row.lastUsedAt) : t("authorizations.never")}
        </Text>
      ),
    },
    {
      key: "status",
      header: t("authorizations.columns.status"),
      render: (row) =>
        needsReauth(row) ? (
          <Badge variant="warning">{t("authorizations.status.reauth")}</Badge>
        ) : (
          <Badge variant="success">{t("authorizations.status.active")}</Badge>
        ),
    },
  ];
}

/** /admin/authorizations: read-only, org-wide list of delegated background access. */
export function AuthorizationsPage() {
  const { t } = useTranslation("adminPlatform");
  const authorizations = useQuery(listAllAuthorizationsOptions());
  const columns = useColumns();
  return (
    <AdminPage title={t("authorizations.title")} subtitle={t("authorizations.subtitle")}>
      <Text variant="body-sm" tone="secondary" className="max-w-3xl">
        {t("authorizations.intro")}
      </Text>
      {authorizations.isError ? (
        <Text tone="error" role="alert">
          {t("authorizations.loadFailed")}
        </Text>
      ) : (
        <DataTable
          aria-label={t("authorizations.tableLabel")}
          columns={columns}
          rows={authorizations.data?.data ?? []}
          rowKey={(row) => row.uuid}
          loading={authorizations.isPending}
          empty={
            <EmptyState
              icon={<KeyRound size={32} />}
              title={t("authorizations.empty")}
              body={t("authorizations.emptyBody")}
            />
          }
        />
      )}
    </AdminPage>
  );
}
